// Fecha de calendario sin hora ('AAAA-MM-DD', como las columnas `date` de la base).
// `new Date('2026-10-02')` la toma como medianoche UTC y en Chile se muestra el día 1 (H2);
// se construye en hora local para que el día no se corra. Un instante con hora o zona se deja igual.
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Piso 1900: hay decretos leyes desde 1974 y convenios desde 1978 (D-17).
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

const toValidDate = (date: string | Date | null | undefined): Date | null => {
  if (!date) return null;
  const match = typeof date === 'string' ? CALENDAR_DATE.exec(date.trim()) : null;
  const d = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(date);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  return d;
};

export const formatDate = (date: string | Date | null | undefined): string => {
  const d = toValidDate(date);
  if (!d) return 'Fecha no disponible';

  return d.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatShortDate = (date: string | Date | null | undefined): string => {
  const d = toValidDate(date);
  if (!d) return 'N/A';

  return d.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const formatDateTime = (date: string | Date | null | undefined): string => {
  const d = toValidDate(date);
  if (!d) return 'N/A';

  return d.toLocaleString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDocumentId = (tipo: string, year: number, numero: string, materia?: string): string => {
  if (tipo === 'oficio' && materia) {
    return `${tipo}-${materia}-${year}-${numero}`;
  }
  return `${tipo}-${year}-${numero}`;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Helper function to generate clean readable summary from structured JSON
 * Industry best practice: Use AI-generated resumen field (80-120 words) as primary
 * with proper fallback chain based on document type
 */
export const generateCleanSummary = (aiSummary: any, tipo: string): string => {
  if (!aiSummary || typeof aiSummary !== 'object') {
    return 'Sin resumen disponible';
  }

  const tipoLower = tipo?.toLowerCase() || '';

  // Primary: Use the AI-generated resumen if available
  if (aiSummary.resumen && aiSummary.resumen.trim()) {
    return stripJsonArtifacts(aiSummary.resumen);
  }

  // Fallback: Use the most relevant field based on document type (clean text, no labels)
  if (tipoLower === 'circular') {
    if (aiSummary.criterio_principal && aiSummary.criterio_principal.trim()) {
      return stripJsonArtifacts(aiSummary.criterio_principal);
    }
  } else if (tipoLower === 'oficio') {
    if (aiSummary.criterio_aplicable && aiSummary.criterio_aplicable.trim()) {
      return stripJsonArtifacts(aiSummary.criterio_aplicable);
    }
  } else if (tipoLower === 'resolucion' || tipoLower === 'resolucion exenta') {
    if (aiSummary.objeto && aiSummary.objeto.trim()) {
      return stripJsonArtifacts(aiSummary.objeto);
    }
  }

  // Final fallback: try any available content field
  const fallbackFields = ['criterio_aplicable', 'criterio_principal', 'objeto', 'materia', 'conclusion'];
  for (const field of fallbackFields) {
    if (aiSummary[field] && aiSummary[field].trim()) {
      return stripJsonArtifacts(aiSummary[field]);
    }
  }

  return 'Sin resumen disponible';
};

/**
 * Resumen estructurado de un documento: `ai_summary` si existe y, si no, `resumen` cuando
 * viene guardado como JSON (H1: 5.070 documentos lo tienen así). Si nada es JSON, devuelve {}.
 */
export const parseAiSummary = (aiSummaryField: unknown, resumen?: string | null): Record<string, any> => {
  let aiSummary: Record<string, any> = {};
  try {
    if (typeof aiSummaryField === 'string' && aiSummaryField) {
      aiSummary = JSON.parse(aiSummaryField);
    } else if (aiSummaryField && typeof aiSummaryField === 'object') {
      aiSummary = aiSummaryField as Record<string, any>;
    }
  } catch {
    aiSummary = {};
  }
  if (Object.keys(aiSummary).length === 0 && typeof resumen === 'string' && resumen.trim().startsWith('{')) {
    try {
      aiSummary = JSON.parse(resumen);
    } catch {
      // No es JSON: se muestra como texto plano
    }
  }
  return aiSummary && typeof aiSummary === 'object' ? aiSummary : {};
};

/** Texto de resumen para mostrar: limpio si es estructurado; si no, el `resumen` tal cual. */
export const getSummaryText = (aiSummary: Record<string, any>, resumen: string | null | undefined, tipo: string): string =>
  Object.keys(aiSummary).length > 0
    ? generateCleanSummary(aiSummary, tipo)
    : resumen || 'Sin resumen disponible';

/**
 * Strip JSON-like artifacts from text (e.g., {"materia": or ,"criterio_aplicable":)
 * These can appear when AI summaries contain raw JSON fragments
 */
export const stripJsonArtifacts = (text: string): string => {
  if (!text) return text;

  // Remove common JSON key patterns
  const patterns = [
    /\{"?materia"?\s*:\s*"?/gi,
    /,?\s*"?criterio_aplicable"?\s*:\s*"?/gi,
    /,?\s*"?criterio_principal"?\s*:\s*"?/gi,
    /,?\s*"?resumen"?\s*:\s*"?/gi,
    /,?\s*"?objeto"?\s*:\s*"?/gi,
    /,?\s*"?fundamento"?\s*:\s*"?/gi,
    /,?\s*"?conclusion"?\s*:\s*"?/gi,
    /,?\s*"?articulos_relevantes"?\s*:\s*"?/gi,
    /,?\s*"?legislacion"?\s*:\s*"?/gi,
    /"\s*,?\s*$/g,  // Trailing quotes and commas
    /^\s*"?\s*/g,   // Leading quotes
    /"\s*}\s*$/g,   // Trailing "}
  ];

  let cleaned = text;
  for (const pattern of patterns) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Clean up multiple spaces and trim
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
};

/**
 * Extract meaningful title from AI summary based on document type
 */
export const extractMeaningfulTitle = (aiSummary: any, tipo: string, defaultTitle: string): string => {
  if (!aiSummary || typeof aiSummary !== 'object') {
    return defaultTitle;
  }

  const tipoLower = tipo?.toLowerCase() || '';

  if (tipoLower === 'circular' && aiSummary.materia) {
    // For circulares, use the materia from AI summary
    return aiSummary.materia;
  } else if ((tipoLower === 'resolucion' || tipoLower === 'resolucion exenta') && aiSummary.objeto) {
    // For resoluciones, use the objeto (what it approves/establishes)
    return aiSummary.objeto;
  }

  return defaultTitle;
};

/**
 * Extract a relevant excerpt from text based on search keywords
 */
export const getRelevantExcerpt = (text: string | undefined | null, query: string, maxLength: number = 400): string => {
  if (!text) return '';
  if (!query) return text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');

  // Split query into terms, ignore short words
  const terms = query.toLowerCase().split(/\s+/).filter(t => t.length > 3);
  if (terms.length === 0) return text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');

  const textLower = text.toLowerCase();
  
  // Find first index of any term
  let firstIndex = -1;
  for (const term of terms) {
    const idx = textLower.indexOf(term);
    if (idx !== -1 && (firstIndex === -1 || idx < firstIndex)) {
      firstIndex = idx;
    }
  }

  if (firstIndex === -1) {
    return text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');
  }

  // Extract surrounding text
  const start = Math.max(0, firstIndex - 60);
  const end = Math.min(text.length, start + maxLength);
  
  let excerpt = text.substring(start, end);
  
  // Clean up boundaries
  if (start > 0) excerpt = '...' + excerpt;
  if (end < text.length) excerpt = excerpt + '...';
  
  return excerpt;
};
